import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { ShoppingBag, LogOut, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

const desktopLink = ({ isActive }) =>
    `uppercase tracking-widest text-xs px-3 py-2 border-b-2 transition-colors font-semibold ${
        isActive ? "border-black text-black" : "border-transparent text-neutral-500 hover:text-black"
    }`;

const mobileLink = ({ isActive }) =>
    `flex items-center px-4 py-3 border-l-2 uppercase tracking-widest text-xs font-semibold ${
        isActive ? "border-black bg-neutral-50 text-black" : "border-transparent text-neutral-600 hover:bg-neutral-50"
    }`;

export default function ClientLayout() {
    const { user, logout } = useAuth();
    const { count } = useCart();
    const nav = useNavigate();
    const [menuOpen, setMenuOpen] = useState(false);

    const brandColor = user?.brand_color || "#0A0A0A";
    const brandName = user?.brand_display_name || user?.company || user?.name || "Client";

    const doLogout = () => {
        logout();
        nav("/login");
    };

    const NAV_LINKS = [
        { to: "/shop", label: "Catalog", testid: "client-nav-shop" },
        { to: "/my-orders", label: "My Orders", testid: "client-nav-orders" },
        { to: "/designs", label: "Designs", testid: "client-nav-designs" },
    ];

    return (
        <div className="min-h-screen bg-white flex flex-col">
            {/* Client brand strip */}
            <div className="w-full h-1" style={{ background: brandColor }}/>

            <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 h-14 md:h-16 flex items-center justify-between gap-3">
                    {/* Left cluster */}
                    <div className="flex items-center gap-3 md:gap-6 min-w-0">
                        {/* Mobile hamburger */}
                        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                            <SheetTrigger asChild>
                                <button
                                    className="md:hidden h-9 w-9 -ml-2 inline-flex items-center justify-center border border-transparent hover:border-black"
                                    data-testid="client-menu-toggle"
                                    aria-label="Open menu"
                                >
                                    <Menu className="h-5 w-5" strokeWidth={1.5}/>
                                </button>
                            </SheetTrigger>
                            <SheetContent side="left" className="w-72 p-0 rounded-none border-r border-black">
                                <div className="p-5 border-b border-border" style={{ background: brandColor + "0d" }}>
                                    <div className="font-display text-2xl tracking-tight leading-none">A V Corp<span className="text-yellow-400">.</span></div>
                                    <div className="mt-3 flex items-center gap-2">
                                        <div className="h-2.5 w-2.5" style={{ background: brandColor }}/>
                                        <div>
                                            <div className="overline text-neutral-500 leading-none">Buying House</div>
                                            <div className="font-display text-base leading-none mt-0.5">{brandName}</div>
                                        </div>
                                    </div>
                                </div>
                                <nav className="py-2">
                                    {NAV_LINKS.map((l) => (
                                        <NavLink
                                            key={l.to}
                                            to={l.to}
                                            className={mobileLink}
                                            data-testid={l.testid + "-mobile"}
                                            onClick={() => setMenuOpen(false)}
                                        >
                                            {l.label}
                                        </NavLink>
                                    ))}
                                </nav>
                                <div className="p-4 border-t border-border mt-2">
                                    <div className="overline text-neutral-500">Signed in</div>
                                    <div className="font-body text-sm">ID {user?.login_id}</div>
                                    <Button
                                        onClick={() => { setMenuOpen(false); doLogout(); }}
                                        variant="outline"
                                        size="sm"
                                        className="w-full mt-3 rounded-none border-black text-black hover:bg-black hover:text-white uppercase text-[10px] tracking-widest"
                                        data-testid="client-logout-btn-mobile"
                                    >
                                        <LogOut className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5}/> Exit
                                    </Button>
                                </div>
                            </SheetContent>
                        </Sheet>

                        <div className="font-display text-xl md:text-2xl tracking-tight leading-none truncate">A V Corp<span className="text-yellow-400">.</span></div>
                        <div className="hidden md:flex items-center gap-2 border-l border-border pl-6">
                            <div className="h-2.5 w-2.5" style={{ background: brandColor }} data-testid="client-brand-dot"/>
                            <div>
                                <div className="overline text-neutral-500 leading-none">Buying House</div>
                                <div className="font-display text-base leading-none mt-0.5" data-testid="client-brand-name">{brandName}</div>
                            </div>
                        </div>
                        <nav className="hidden md:flex items-center gap-1 ml-4">
                            {NAV_LINKS.map((l) => (
                                <NavLink key={l.to} to={l.to} className={desktopLink} data-testid={l.testid}>{l.label}</NavLink>
                            ))}
                        </nav>
                    </div>

                    {/* Right cluster */}
                    <div className="flex items-center gap-3 md:gap-4">
                        <div className="hidden sm:block text-right">
                            <div className="overline text-neutral-500">Account</div>
                            <div className="font-body text-xs">ID {user?.login_id}</div>
                        </div>
                        <div className="relative">
                            <NavLink to="/shop" className="inline-flex" data-testid="cart-indicator">
                                <ShoppingBag className="h-5 w-5" strokeWidth={1.5}/>
                                {count > 0 && (
                                    <span className="absolute -top-2 -right-2 text-white text-[10px] w-5 h-5 flex items-center justify-center font-mono" style={{ background: brandColor }}>{count > 99 ? "99+" : count}</span>
                                )}
                            </NavLink>
                        </div>
                        <Button
                            onClick={doLogout}
                            variant="outline"
                            size="sm"
                            data-testid="client-logout-btn"
                            className="hidden md:inline-flex rounded-none border-black text-black hover:bg-black hover:text-white uppercase text-[10px] tracking-widest"
                        >
                            <LogOut className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.5}/> Exit
                        </Button>
                    </div>
                </div>
                {/* Mobile brand strip under header */}
                <div className="md:hidden px-4 pb-2 flex items-center gap-2">
                    <div className="h-2 w-2" style={{ background: brandColor }}/>
                    <div className="overline text-neutral-500">Curated for</div>
                    <div className="font-display text-xs leading-none" style={{ color: brandColor }}>{brandName}</div>
                </div>
            </header>
            <main className="flex-1">
                <Outlet />
            </main>
            <footer className="border-t border-border py-6 text-center text-xs text-neutral-500 font-body px-4">
                A V Corp · Curated for <span style={{ color: brandColor, fontWeight: 600 }}>{brandName}</span> · © 2026
            </footer>
        </div>
    );
}
